import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { fetchToegangscontroles, toggleToegangscontrole, testToegangscontrole, startAuthorizationFlow, refreshToken } from './Reducers/actions';
import ToegangscontroleForm from './Components/ToegangscontroleForm';
import moment from 'moment';

const ToegangscontroleIndex = ({ 
  toegangscontroles, 
  fetchToegangscontroles, 
  toggleToegangscontrole, 
  testToegangscontrole,
  startAuthorizationFlow,
  refreshToken,
  error, 
  testResult 
}) => {
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState({ show: false, provider: null });

  useEffect(() => {
    loadToegangscontroles();
  }, []);

  const loadToegangscontroles = async () => {
    setLoading(true);
    await fetchToegangscontroles();
    setLoading(false);
  };

  const handleToggle = async (id) => {
    await toggleToegangscontrole(id);
    await loadToegangscontroles();
  };

  const handleTest = async (id) => {
    await testToegangscontrole(id);
  };

  const handleStartAuthorization = async (id) => {
    await startAuthorizationFlow(id);
  };

  const handleRefreshToken = async (id) => {
    await refreshToken(id);
    await loadToegangscontroles();
  };

  const handleConfigure = (provider = 'salto-ks') => {
    setShowForm({ show: true, provider });
  };

  const handleFormClose = () => {
    setShowForm({ show: false, provider: null });
    loadToegangscontroles();
  };

  const getSaltoConfig = () => {
    return toegangscontroles.find(t => t.naam === 'salto-ks');
  };

  const getDoorcyConfig = () => {
    return toegangscontroles.find(t => t.naam === 'doorcy');
  };

  const getStatusBadge = (config) => {
    if (!config) return <span className="badge bg-secondary">Niet geconfigureerd</span>;
    
    switch (config.status) {
      case 'niet_geconfigureerd':
        return <span className="badge bg-secondary">Niet geconfigureerd</span>;
      case 'geconfigureerd':
        return <span className="badge bg-warning">Geconfigureerd</span>;
      case 'geautoriseerd':
        return <span className="badge bg-success">Geautoriseerd</span>;
      case 'fout':
        return <span className="badge bg-danger">Fout</span>;
      default:
        return <span className="badge bg-secondary">Onbekend</span>;
    }
  };

  const isTokenExpired = (config) => {
    if (!config?.oauth_tokens?.expires_at) return true;
    return new Date() >= new Date(config.oauth_tokens.expires_at);
  };

  const isTokenExpiringSoon = (config) => {
    if (!config?.oauth_tokens?.expires_at) return false;
    const fiveMinutesFromNow = new Date();
    fiveMinutesFromNow.setMinutes(fiveMinutesFromNow.getMinutes() + 5);
    return fiveMinutesFromNow >= new Date(config.oauth_tokens.expires_at);
  };

  if (loading) {
    return (
      <div className="box box-default">
        <div className="box-body text-center">
          <i className="fas fa-spinner fa-spin"></i> Laden...
        </div>
      </div>
    );
  }

  const saltoConfig = getSaltoConfig();

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="box box-default">
            <div className="box-header with-border">
              <h3 className="box-title">
                <i className="fas fa-lock me-2"></i>
                Toegangscontrole
              </h3>
            </div>
            
            <div className="box-body">
              {error && (
                <div className="alert alert-danger">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {error}
                </div>
              )}

              <div className="row">
                {/* Salto KS Card */}
                <div className="col-md-12 mb-4">
                  <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">
                        <i className="fas fa-key me-2 text-primary"></i>
                        Salto KS Connect API
                      </h5>
                      <div>
                        {getStatusBadge(saltoConfig)}
                        {saltoConfig?.actief && (
                          <span className="badge bg-success ms-2">Actief</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="card-body">
                      <p className="text-muted">
                        Integratie met Salto KS voor toegangscontrole en sleutelbeheer via OAuth2/OpenID Connect.
                      </p>
                      
                      {saltoConfig && (
                        <>
                          <div className="row mb-3">
                            <div className="col-md-6">
                              <small className="text-muted d-block">Laatste update:</small>
                              <strong>{moment(saltoConfig.bijgewerkt).fromNow()}</strong>
                            </div>
                            <div className="col-md-6">
                              <small className="text-muted d-block">Environment:</small>
                              <strong className={`text-${saltoConfig.configuratie?.environment === 'production' ? 'success' : 'warning'}`}>
                                {saltoConfig.configuratie?.environment === 'production' ? 'Productie' : 'Sandbox'}
                              </strong>
                            </div>
                          </div>

                          {saltoConfig.oauth_tokens?.expires_at && (
                            <div className="row mb-3">
                              <div className="col-12">
                                <small className="text-muted d-block">Token status:</small>
                                {isTokenExpired(saltoConfig) ? (
                                  <span className="text-danger">
                                    <i className="fas fa-exclamation-triangle me-1"></i>
                                    Token verlopen op {moment(saltoConfig.oauth_tokens.expires_at).format('DD-MM-YYYY HH:mm')}
                                  </span>
                                ) : isTokenExpiringSoon(saltoConfig) ? (
                                  <span className="text-warning">
                                    <i className="fas fa-clock me-1"></i>
                                    Token verloopt binnenkort: {moment(saltoConfig.oauth_tokens.expires_at).fromNow()}
                                  </span>
                                ) : (
                                  <span className="text-success">
                                    <i className="fas fa-check me-1"></i>
                                    Token geldig tot {moment(saltoConfig.oauth_tokens.expires_at).format('DD-MM-YYYY HH:mm')}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}

                          {saltoConfig.fout_bericht && (
                            <div className="alert alert-danger">
                              <i className="fas fa-exclamation-circle me-2"></i>
                              {saltoConfig.fout_bericht}
                            </div>
                          )}
                        </>
                      )}

                      <div className="btn-group w-100" role="group">
                        <button 
                          className="btn btn-primary btn-sm"
                          onClick={() => handleConfigure('salto-ks')}
                        >
                          <i className="fas fa-cog me-1"></i>
                          Configureren
                        </button>
                        
                        {saltoConfig && (
                          <>
                            <button 
                              className={`btn btn-sm ${saltoConfig.actief ? 'btn-warning' : 'btn-success'}`}
                              onClick={() => handleToggle(saltoConfig._id)}
                            >
                              <i className={`fas ${saltoConfig.actief ? 'fa-pause' : 'fa-play'} me-1`}></i>
                              {saltoConfig.actief ? 'Uitschakelen' : 'Inschakelen'}
                            </button>
                            
                            {saltoConfig.status === 'geconfigureerd' && (
                              <button 
                                className="btn btn-info btn-sm"
                                onClick={() => handleStartAuthorization(saltoConfig._id)}
                              >
                                <i className="fas fa-unlock me-1"></i>
                                Autoriseren
                              </button>
                            )}
                            
                            {saltoConfig.status === 'geautoriseerd' && (
                              <>
                                <button 
                                  className="btn btn-info btn-sm"
                                  onClick={() => handleTest(saltoConfig._id)}
                                >
                                  <i className="fas fa-vial me-1"></i>
                                  Testen
                                </button>
                                
                                {(isTokenExpired(saltoConfig) || isTokenExpiringSoon(saltoConfig)) && (
                                  <button 
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => handleRefreshToken(saltoConfig._id)}
                                  >
                                    <i className="fas fa-sync me-1"></i>
                                    Token Vernieuwen
                                  </button>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Doorcy Card */}
                <div className="col-md-12 mb-4">
                  <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">
                        <i className="fas fa-door-open me-2 text-success"></i>
                        Doorcy API
                      </h5>
                      <div>
                        {getStatusBadge(getDoorcyConfig())}
                        {getDoorcyConfig()?.actief && (
                          <span className="badge bg-success ms-2">Actief</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="card-body">
                      <p className="text-muted">
                        Integratie met Doorcy voor digitale toegangscontrole en sleutelbeheer via API key authenticatie.
                      </p>
                      
                      {getDoorcyConfig() && (
                        <>
                          <div className="row mb-3">
                            <div className="col-md-6">
                              <small className="text-muted d-block">Laatste update:</small>
                              <strong>{moment(getDoorcyConfig().bijgewerkt).fromNow()}</strong>
                            </div>
                            <div className="col-md-6">
                              <small className="text-muted d-block">Authentication:</small>
                              <strong className="text-info">
                                {getDoorcyConfig().configuratie?.auth_type?.replace('_', ' ').toUpperCase() || 'API Key'}
                              </strong>
                            </div>
                          </div>

                          {getDoorcyConfig().fout_bericht && (
                            <div className="alert alert-danger">
                              <i className="fas fa-exclamation-circle me-2"></i>
                              {getDoorcyConfig().fout_bericht}
                            </div>
                          )}
                        </>
                      )}

                      <div className="btn-group w-100" role="group">
                        <button 
                          className="btn btn-primary btn-sm"
                          onClick={() => handleConfigure('doorcy')}
                        >
                          <i className="fas fa-cog me-1"></i>
                          Configureren
                        </button>
                        
                        {getDoorcyConfig() && (
                          <>
                            <button 
                              className={`btn btn-sm ${getDoorcyConfig().actief ? 'btn-warning' : 'btn-success'}`}
                              onClick={() => handleToggle(getDoorcyConfig()._id)}
                            >
                              <i className={`fas ${getDoorcyConfig().actief ? 'fa-pause' : 'fa-play'} me-1`}></i>
                              {getDoorcyConfig().actief ? 'Uitschakelen' : 'Inschakelen'}
                            </button>
                            
                            <button 
                              className="btn btn-info btn-sm"
                              onClick={() => handleTest(getDoorcyConfig()._id)}
                            >
                              <i className="fas fa-vial me-1"></i>
                              Testen
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Test Results */}
              {testResult && (
                <div className={`alert ${testResult.success ? 'alert-success' : 'alert-danger'}`}>
                  <i className={`fas ${testResult.success ? 'fa-check-circle' : 'fa-exclamation-circle'} me-2`}></i>
                  <strong>Test resultaat:</strong> {testResult.message}
                  {testResult.user_info && (
                    <div className="mt-2">
                      <small>Gebruiker: {testResult.user_info.name || testResult.user_info.email}</small>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Configuration Modal */}
      {showForm.show && (
        <ToegangscontroleForm
          onClose={handleFormClose}
          provider={showForm.provider}
          existingConfig={showForm.provider === 'doorcy' ? getDoorcyConfig() : getSaltoConfig()}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  toegangscontroles: state.toegangscontrole?.toegangscontroles || [],
  error: state.toegangscontrole?.error,
  testResult: state.toegangscontrole?.testResult
});

export default connect(mapStateToProps, {
  fetchToegangscontroles,
  toggleToegangscontrole,
  testToegangscontrole,
  startAuthorizationFlow,
  refreshToken
})(ToegangscontroleIndex);